import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Text,
  useToast,
} from '@chakra-ui/react';
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import axios from 'axios';
import FileSaver from 'file-saver';
import React, { useMemo, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import {
  CreditAgreement,
  typeguards,
  Util,
} from '@finley-technologies/finley-types';

import { useGetGeneratedAssignedLoanTapeDownloadUrlLazyQueryGQLType } from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { NumericBaseFieldChart } from './NumericBaseFieldChart';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { BasicTable } from 'packages/common-frontend/components/TanStack';
import {
  errorToastId,
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { formatBaseFieldValue } from 'packages/common-frontend/utils';
import { ChartTimePeriod } from 'packages/portfolio/ui/shared/portfolioChartUtils';

type BaseFieldHistoryTableProps = {
  historicalRecords: any[];
  baseFieldName: string;
  baseFieldType: CreditAgreement.DataTapeField.Type;
  downloadFile: (reportingDate: Util.FullDateStr, fileVersion: number) => void;
  loanTapeDownloadCellId?: string;
  setLoanTapeDownloadCellId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
};
const BaseFieldHistoryTable = ({
  historicalRecords,
  baseFieldName,
  baseFieldType,
  downloadFile,
  loanTapeDownloadCellId,
  setLoanTapeDownloadCellId,
}: BaseFieldHistoryTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns: ColumnDef<any, any>[] = [
    {
      id: 'reportingDate',
      accessorKey: 'reportingdate',
      header: function formatHeader() {
        return (
          <Text color="black" fontSize="16px">
            Reporting Date
          </Text>
        );
      },
      cell: ({ getValue }) =>
        commonDate.fullDateStrToHumanDateString(getValue()),
    },
    {
      id: 'field',
      accessorFn: (row) => `${row[`bf.${baseFieldName}`]}`,
      header: function formatHeader() {
        return (
          <Text color="black" fontSize="16px">
            Field
          </Text>
        );
      },
      cell: ({ getValue }) => formatBaseFieldValue(getValue(), baseFieldType),
    },
    {
      id: 'loanTape',
      header: function formatHeader() {
        return (
          <Text color="black" fontSize="16px">
            Loan Tape
          </Text>
        );
      },
      /* eslint-disable react/display-name */
      cell: ({ cell, row }) => {
        const uniqueCellId = `${cell.id}-${baseFieldName}`;
        return (
          <Button
            variant="link"
            color={colors.info}
            leftIcon={<Icon icon="download" color={colors.info} />}
            _focus={{ border: 'none' }}
            _hover={{ textDecoration: 'underline !important' }}
            style={{ textDecoration: 'none' }}
            isLoading={loanTapeDownloadCellId === uniqueCellId}
            isDisabled={
              loanTapeDownloadCellId !== undefined &&
              loanTapeDownloadCellId !== uniqueCellId
            }
            onClick={() => {
              setLoanTapeDownloadCellId(uniqueCellId);
              downloadFile(
                row.original.reportingdate,
                row.original.fileversion,
              );
            }}
          >
            Download
          </Button>
        );
      },
      enableSorting: false,
    },
  ];

  const data = useMemo(() => {
    return historicalRecords.slice().reverse();
  }, [historicalRecords]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return <BasicTable table={table} />;
};

type BaseFieldAccordionProps = {
  historicalRecords: any[];
  baseFieldName: string;
  baseFieldType: CreditAgreement.DataTapeField.Type;
  selectedTimePeriod: ChartTimePeriod;
  downloadFile: (reportingDate: Util.FullDateStr, fileVersion: number) => void;
  loanTapeDownloadCellId?: string;
  setLoanTapeDownloadCellId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
};
const BaseFieldAccordion = ({
  historicalRecords,
  baseFieldName,
  baseFieldType,
  selectedTimePeriod,
  downloadFile,
  loanTapeDownloadCellId,
  setLoanTapeDownloadCellId,
}: BaseFieldAccordionProps) => {
  return (
    <Accordion allowMultiple>
      <AccordionItem background="white" borderRadius="8px" border="none">
        {({ isExpanded }) => (
          <>
            <AccordionButton
              padding="40px"
              borderRadius={isExpanded ? '8px 8px 0px 0px' : '8px'}
              border="1px"
              borderColor={colors.gray[200]}
              borderBottom={isExpanded ? 'none' : 'auto'}
              _focus={{ borderBottom: isExpanded ? 'none' : 'auto' }}
              _hover={{ background: 'white' }}
            >
              <Flex
                width="100%"
                alignItems="center"
                justifyContent="space-between"
              >
                <Flex alignItems="center">
                  <Text fontSize="xl" fontWeight="semibold" mr="24px">
                    {baseFieldName}
                  </Text>
                  <Box
                    borderRadius="8px"
                    padding="8px 16px"
                    background="gray.200"
                  >
                    <Text fontWeight="500">
                      {formatBaseFieldValue(
                        historicalRecords[historicalRecords.length - 1][
                          `bf.${baseFieldName}`
                        ],
                        baseFieldType,
                      )}
                    </Text>
                  </Box>
                </Flex>
                <Icon
                  icon={
                    isExpanded ? 'circle-chevron-up' : 'circle-chevron-down'
                  }
                  size="large"
                  color={colors.aurora[400]}
                />
              </Flex>
            </AccordionButton>
            <AccordionPanel
              padding="0px 40px 40px"
              borderRadius="0px 0px 8px 8px"
              border="1px"
              borderColor={colors.gray[200]}
              borderTop="none"
            >
              {typeguards.creditAgreement.isNumericType(baseFieldType) && (
                <NumericBaseFieldChart
                  historicalRecords={historicalRecords}
                  baseFieldName={baseFieldName}
                  baseFieldType={baseFieldType}
                  selectedTimePeriod={selectedTimePeriod}
                />
              )}
              <BaseFieldHistoryTable
                historicalRecords={historicalRecords}
                baseFieldName={baseFieldName}
                baseFieldType={baseFieldType}
                downloadFile={downloadFile}
                loanTapeDownloadCellId={loanTapeDownloadCellId}
                setLoanTapeDownloadCellId={setLoanTapeDownloadCellId}
              />
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
};

type CollateralDataQualityProps = {
  historicalRecords: any[];
  loanProductMechanic: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities;
  creditFacilityId: string;
  creditFacilitySlug: string;
  selectedTimePeriod: ChartTimePeriod;
};
export const CollateralDataQuality = ({
  historicalRecords,
  loanProductMechanic,
  creditFacilityId,
  creditFacilitySlug,
  selectedTimePeriod,
}: CollateralDataQualityProps) => {
  const toast = useToast();

  const [downloadFileName, setDownloadFileName] = useState<string>();

  // determines which cell should have a loading spinner
  // rest of download buttons should be disabled
  // only allow single download at a time
  const [
    loanTapeDownloadCellId,
    setLoanTapeDownloadCellId,
  ] = useState<string>();

  const [
    getGeneratedAssignedLoanTapeDownloadUrl,
  ] = useGetGeneratedAssignedLoanTapeDownloadUrlLazyQueryGQLType({
    fetchPolicy: 'no-cache',
    onCompleted: (response) => {
      if (
        response &&
        response.getGeneratedAssignedLoanTapeDownloadUrl &&
        downloadFileName
      ) {
        const { getGeneratedAssignedLoanTapeDownloadUrl: url } = response;
        axios({
          url,
          method: 'GET',
          responseType: 'blob',
        })
          .then((res) => {
            const fileBlob = new Blob([res.data], {
              type: res.data.type,
            });
            FileSaver.saveAs(fileBlob, downloadFileName);
            successToastConfig.title = `Successfully downloaded ${downloadFileName}`;
            toast(successToastConfig);
            setLoanTapeDownloadCellId(undefined);
            setDownloadFileName(undefined);
          })
          .catch(() => {
            if (!toast.isActive(errorToastId)) {
              errorToastConfig.title = `Error downloading ${downloadFileName}`;
              toast(errorToastConfig);
            }
            setLoanTapeDownloadCellId(undefined);
            setDownloadFileName(undefined);
          });
      }
    },
    onError: () => {
      setLoanTapeDownloadCellId(undefined);
    },
  });

  const downloadFile = (
    reportingDate: Util.FullDateStr,
    fileVersion: number,
  ) => {
    setDownloadFileName(
      `${
        loanProductMechanic.entity.slug
      }-${creditFacilitySlug}-${reportingDate}${
        fileVersion > 1 ? `-v${fileVersion}` : ''
      }.csv`,
    );
    getGeneratedAssignedLoanTapeDownloadUrl({
      variables: {
        creditFacilityId,
        loanProductId: loanProductMechanic.entity.id,
        reportingDate,
        fileVersion,
      },
    });
  };

  return (
    <Accordion allowMultiple defaultIndex={[0]}>
      <AccordionItem
        backgroundColor={colors.background[50]}
        mt="40px"
        border="none"
        borderRadius="8px"
      >
        {({ isExpanded }) => (
          <>
            <AccordionButton
              padding="40px"
              borderRadius="8px"
              _focus={{ border: 'none' }}
              _hover={{ background: colors.background[50] }}
            >
              <Flex
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Text fontSize="2xl" fontWeight="semibold">
                  Collateral Data Quality
                </Text>
                <Icon
                  icon={
                    isExpanded ? 'circle-chevron-up' : 'circle-chevron-down'
                  }
                  size="large"
                  color={colors.aurora[400]}
                />
              </Flex>
            </AccordionButton>
            <AccordionPanel
              padding="0px 40px 40px"
              maxH="1000px"
              overflowY="auto"
            >
              {loanProductMechanic._config_.baseFieldConfigs.map(
                (baseFieldConfig, index) => (
                  <Box
                    key={baseFieldConfig.field}
                    mt={index === 0 ? '0px' : '24px'}
                  >
                    <BaseFieldAccordion
                      historicalRecords={historicalRecords}
                      baseFieldName={baseFieldConfig.field}
                      baseFieldType={baseFieldConfig.type}
                      selectedTimePeriod={selectedTimePeriod}
                      downloadFile={downloadFile}
                      loanTapeDownloadCellId={loanTapeDownloadCellId}
                      setLoanTapeDownloadCellId={setLoanTapeDownloadCellId}
                    />
                  </Box>
                ),
              )}
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
};
